/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {
    ConfigurationData,
    Debug,
    EEmbededElementTypes,
    EPartsTypes,
    LightingSettingsData,
    Messenger,
    Model as RenderModel,
    RenderDocument,
    ZeropointSettings,
    RenderElement,
    BoundingBox as RenderBoundingBox
} from "js_renderer";
import {mat4, vec3} from "gl-matrix";
import ModelsAggregation from "./models_aggregation";
import CameraAnimation from "../camera_animation";
import BoundingBox from "../bounding_box";
import IAbstractElement from "../elements/abstract_element_interface";
import IScene from "../scene_interface";
import IModel from "./model_interface";
import Factory from "../elements/factory";
import IBoundingBox from "../bounding_box_interface";
import {TViewPoint} from "./model_types";

/*
 *  SceneAPI.Model is wrapper for the cadwork.webgl.Model class
 */
export default
class Model implements IModel {

    protected static UID: number = 1;

    protected _renderDocument: RenderDocument;
    protected _UID: number;
    protected _renderModel: RenderModel;
    protected _transformation: mat4;
    protected _modelTransformation: mat4;
    protected _isMainModel: boolean;
    protected _modelAggregation: ModelsAggregation;
    protected modelUrl: string;
    protected descriptionUrl: string;
    protected hiddenByDefault: boolean;
    protected showHideSettings: boolean;
    protected _manipulationAllowed: boolean;
    protected _selectionAllowed: boolean;

    constructor(aRenderDocument: RenderDocument, aModelUrl: string, aDescriptionUrl: string, aHiddenByDefault: boolean, aIsMainModel: boolean) {
        this._renderDocument = aRenderDocument;
        this._UID = Model.UID++;
        this._renderModel = null;
        this._transformation = null;
        this._modelTransformation = mat4.create();
        this._isMainModel = aIsMainModel === undefined ? true : aIsMainModel;

        this._modelAggregation = null; // a group (aggregation, not group tree item) of models to which this model is assigned

        this.modelUrl = aModelUrl;
        this.descriptionUrl = aDescriptionUrl;

        this.hiddenByDefault = aHiddenByDefault === undefined ? false : aHiddenByDefault;
        this.showHideSettings = !this.hiddenByDefault;

        this._manipulationAllowed = true;
        this._selectionAllowed = true;
    }

    public getUID(): number {
        return this._UID;
    }

    public getIsMainModel(): boolean {
        return this._isMainModel;
    }

    public getProjectInfo(): any {
        return this._renderModel.getProjectInfo();
    }

    public getAnnotationConstructionGroups(): any {
        return this._renderModel.getAnnotationConstructionGroups();
    }

    public getAnnotation(aFilterObject: any): any {
        return this._renderModel.getAnnotation(aFilterObject);
    }

    public getAnnotationsElements(aFilterObject: string): any[] {
        return this._renderModel.getAnnotationsElements(aFilterObject);
    }

    /** this function points to unfinished function  */
    public getElementsByAnnotations(aAnnotationsIdArray: number[]): any {
        return this._renderModel.getElementsByAnnotations(aAnnotationsIdArray);
    }

    public getCameraAnimations(): CameraAnimation[] {
        let list = [];

        let animations = this._renderModel.modelDescription.animations.getCameraAnimations();
        for (let key in animations) {
            // for each animation we creat a list if scene api animations objects
            let sapiCamAnimation = new CameraAnimation(this);
            sapiCamAnimation.setAnimation(animations[key]);

            list.push(sapiCamAnimation);
        }

        return list;
    }

    public getName(): string {
        return this._renderModel.getName();
    }

    public isInternal(): boolean {
        if (!this._renderModel) {
            return false;
        }

        return this._renderModel.isInternal();
    }

    public getViewpoint(): TViewPoint {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getViewpoint.");
            return null;
        }

        if (typeof this._renderModel.getViewpoint() === 'undefined' || this._renderModel.getViewpoint() === null) {
            return null;
        }

        return {
            position: this._renderModel.getViewpoint().position,
            centerOfRotation: this._renderModel.getViewpoint().centerOfRotation,
            fieldOfView: this._renderModel.getViewpoint().fieldOfView
        };
    }

    /***
     * Set an aggregation object to which this model will be attached. If the aggregate is already set, the the model instance
     * will be detached from current aggreagation and will be attached to tne new aAggregate
     * @param aAggregation
     */
    public attachToAggregation(aAggregation: ModelsAggregation) {
        // detach the model from current aggregation if it is defined
        if (this._modelAggregation) {
            this._modelAggregation.detachObject(this);
        }

        this._modelAggregation = aAggregation;

        // if the aggregation is set, then attach this model to it
        if (this._modelAggregation) {
            this._modelAggregation.attachObject(this);
        }
    }

    public detachFromAggregation() {
        this.attachToAggregation(null);
    }

    public getSBoundingBox(): IBoundingBox {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getBoundingBox.");
            return null;
        }

        let sceneAPIModelBBox = new BoundingBox();
        sceneAPIModelBBox.set(this._renderModel.getBoundingBox());

        return sceneAPIModelBBox;
    }

    public getSBoundingBoxForNPC(): IBoundingBox {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getBoundingBox.");
            return null;
        }

        let sceneAPIModelBBox = new BoundingBox();
        sceneAPIModelBBox.set(this._renderModel.getBoundingBoxForNPC());

        return sceneAPIModelBBox;
    }

    public getSBoundingBoxFromVisibleObjects(): IBoundingBox {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getBoundingBox.");
            return null;
        }

        let renderBBox = this._renderModel.getBoundingBoxFromVisibleObjects();

        let sceneAPIModelBBox = new BoundingBox();
        sceneAPIModelBBox.set(renderBBox);

        return sceneAPIModelBBox;
    }

    public getSBoundingBoxFromVisibleObjectsForNPC(): IBoundingBox {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getBoundingBox.");
            return null;
        }

        let renderBBox = this._renderModel.getBoundingBoxFromVisibleObjectsForNPC();

        let sceneAPIModelBBox = new BoundingBox();
        sceneAPIModelBBox.set(renderBBox);

        return sceneAPIModelBBox;
    }

    public getSGroupBoundingBox(aGroupId: number): IBoundingBox {

        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getBoundingBox.");
            return null;
        }

        let bbox = new RenderBoundingBox();

        let groupTree = this._renderModel.getGroupTree();
        let group = groupTree.findGroup(aGroupId);
        if (group) {
            bbox = group.getBoundingBoxFromAllVisibleObjects();
            if (bbox.isValid()) {
                bbox.extendByBBox(bbox);
            }
        }

        let sceneAPIModelBBox = new BoundingBox();
        sceneAPIModelBBox.set(bbox);

        return sceneAPIModelBBox;
    }

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////// GROUP TREE API ////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    public getGroupTreeStructure(): any {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for findGroup.");
            return null;
        }

        let groupTree = this._renderModel.getGroupTree();
        if (!groupTree) {
            return null;
        }

        return groupTree.serialize();
    }

    public findParentGroup(aGroupID: number): string | null {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for findGroup.");
            return null;
        }

        let groupTree = this._renderModel.getGroupTree();
        if (groupTree) {
            let group = groupTree.findGroup(aGroupID);
            if (!group) {
                return null;
            }

            let parentGroup = group.getParent();
            if (parentGroup) {
                return parentGroup.public.group_id;
            }
        }

        return null;
    }

    public setGroupVisibility(aGroupID: number, aVisible: boolean) {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for setGroupVisibility.");
            return null;
        }

        this._renderModel.setGroupVisibility(aGroupID, aVisible);
    }

    public setVisibility(aValue: boolean) {
        this._renderModel.setVisibility(aValue);
    }

    public getSceneAPI(): IScene {
        return this._renderDocument.getSceneAPI();
    }

    public getGroupVisibility(aGroupID: number): number {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getGroupVisibility.");
            return null;
        }

        return this._renderModel.getGroupVisibility(aGroupID);
    }

    public addGroup(aParentGroup: any, aGroupName: string, aVisible: boolean): number {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for addGroup.");
            return null;
        }

        let uniqueGroupId = this._renderModel.getUniqueGroupId();
        let groupJSON = {
            group_id: uniqueGroupId,
            name: aGroupName,
            visible: aVisible ? 2 : 0,
            expanded: false,
            elements: []
        };

        let groupID = this._renderModel.modelDescription.groupTree.addGroup(aParentGroup, groupJSON);
        Messenger.fireCDWKEvent(this._renderDocument.getSceneAPI().getTargetTag(), "cwx3d_groupAdded", {
            groupID: groupID
        });

        return groupID;
    }

    public removeGroup(aGroupID: number) {
        this._renderModel.modelDescription.groupTree.removeGroup(aGroupID);
        Messenger.fireCDWKEvent(this._renderDocument.getSceneAPI().getTargetTag(), "cwx3d_groupRemoved", {
            groupID: aGroupID
        });
    }

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////// END OF GROUP TREE API /////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    /***
     * Set texture by url to specified elements with defined textureId
     * @param aUrl
     * @param aTextureId
     * @returns {*}
     */
    public setTexture(aUrl: string, aTextureID: WebGLTexture) {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for setTexture.");
            return null;
        }

        let geometryObjects = this._renderModel.getGeometryObjects();
        for (let geometryObject of geometryObjects) {
            if (geometryObject.getPrimitiveSize() === 3) { // we can set texture only for triangle primitives at this moment
                if (!geometryObject.appearance.texture.get().imageTexture) {
                    continue;
                }

                if (geometryObject.appearance.texture.get().urlAsID[0] === aTextureID) {
                    geometryObject.appearance.texture.get().url = [aUrl];

                    // as we do not know the following properties, we reset them to default values
                    geometryObject.appearance.texture.get().width = [0];
                    geometryObject.appearance.texture.get().height = [0];
                    geometryObject.appearance.texture.get().offsets = [0];
                    geometryObject.appearance.texture.get().sizes = [0];
                    geometryObject.appearance.texture.get().tiled = [false];
                    geometryObject.appearance.texture.get().format = aUrl.split('.').pop();
                    geometryObject.appearance.texture.get().alpha = geometryObject.appearance.texture.get().format === "png" ? true : false;
                    // geometryObject.appearance.texture.get().model = "REPLACE"; // inherit value
                    // geometryObject.appearance.texture.get().repeatS = 1; // inherit value
                    // geometryObject.appearance.texture.get().repeatT = 1; // inherit value

                    this._renderDocument.getRenderer().updateShape(geometryObject);
                }
            }
        }
    }

    public addElement(aSceneAPIElement, aGroupID) {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for addElement.");
            return null;
        }
        aSceneAPIElement.attachElementToRenderModel(this._renderModel);
        aSceneAPIElement.attachToRenderGroup(aGroupID);

        aSceneAPIElement.draw();
    }

    public removeElement(aSceneAPIElement: IAbstractElement) {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for removeElement.");
            return null;
        }

        aSceneAPIElement.remove();
    }

    public createEntities(): IAbstractElement[] {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for createEntities.");
            return null;
        }

        let sceneAPIEntities = []; // TB why entities are pushed here even if it is not used anywhere?
                                   // JP well because you may need reference to the created objects
                                   // for e.g. removing them; In general, even if the references
                                   // are not used by the app, shall be returned

        let geElements = this._renderModel.getElementsByType(EEmbededElementTypes.GEOMETRYENTITY);
        // read and convert the poi elements to poi entities for models older and including the v8 deploy
        for (let element of geElements) {
            let entityParts = element.getParts(EPartsTypes.entities).getValues();

            // remove the poi object which has no geometry assigned from the document
            this._renderModel.removeElement(element);

            for (let entityPart of entityParts) {
                let entitySceneElement = Factory.createAPIElement(this._renderDocument, entityPart.type);

                if (entityPart.type === EEmbededElementTypes.POIENTITY ||
                    entityPart.type === EEmbededElementTypes.ANIMATIONENTITY) {

                    entitySceneElement.attachElementToRenderModel(this._renderModel);
                    entitySceneElement.attachToRenderGroup(element.getGroup().getID());

                    entitySceneElement.extendProperties(entityPart);

                    entitySceneElement.setBlendColorFromGroup(element.getGroup());
                    entitySceneElement.setSize(10, 28);
                    entitySceneElement.setPinPoint([0.0, 0.5]);

                    // create the model element and add it to the rendering process
                    entitySceneElement.draw(false);
                    sceneAPIEntities.push(entitySceneElement);
                }
                else if (entityPart.type === EEmbededElementTypes.TOWNENTITY ||
                    entityPart.type === EEmbededElementTypes.MOUNTAINENTITY ||
                    entityPart.type === EEmbededElementTypes.WATERENTITY ||
                    entityPart.type === EEmbededElementTypes.MEASUREPOINTENTITY) {

                    entitySceneElement.attachElementToRenderModel(this._renderModel);
                    // use the group id of replaced model element
                    entitySceneElement.attachToRenderGroup(element.getGroup() ? element.getGroup().getID() : null);

                    entitySceneElement.extendProperties(entityPart);
                    // entitySceneElement.setSize(10, 28);
                    // entitySceneElement.setPinPoint([0.0, 0.5]);

                    // create the model element and add it to the rendering process
                    entitySceneElement.draw(false);
                    sceneAPIEntities.push(entitySceneElement);
                }
            }
        }

        return sceneAPIEntities;
    }

    /**
     * Get all visible entities of specified type.
     *
     * @param {cadwork.webgl.model.elements.EEmbededElementTypes} aType type of searched entities (
     * e.g. cadwork.webgl.model.elements.EEmbededElementTypes.POIENTITY)
     * @returns {Array} array of scene api elements representing the entities
     */
    public getAllVisibleEntities(aType: EEmbededElementTypes): IAbstractElement[] {
        let foundSceneAPIEntities = [];

        let geElements = this._renderModel.getElementsByType(aType);
        for (let element of geElements) {
            let sapiElement = element.getSceneAPIElementReference();
            if (sapiElement && sapiElement.getVisibility()) {
                foundSceneAPIEntities.push(sapiElement);
            }
        }

        return foundSceneAPIEntities;
    }

    public getStandpoints(): any[] {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getStandpoints.");
            return null;
        }

        return this._renderModel.getStandpoints();
    }

    public getViews(): any[] {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getViews.");
            return null;
        }

        return this._renderModel.getViews();
    }

    public getGeoLocationSettings(): any {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getGeoLocationSettings.");
            return null;
        }

        return this._renderModel.getGeoLocationSettings();
    }

    public getGeoLocationInfo(): any {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getGeoLocationSettings.");
            return null;
        }

        return this._renderModel.getGeoLocationInfo();
    }

    /**
     *  Returns model configuration properties
     */
    public getModelConfiguration(): ConfigurationData | null {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getModelConfiguration.");
            return null;
        }

        return this._renderModel.getModelConfiguration();
    }

    public getLightingSettings(): LightingSettingsData | null {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getLightingSettings.");
            return null;
        }

        return this._renderModel.getLightingSettings();
    }

    public getZeropointSettingsRef(): ZeropointSettings | null {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for getZeropointSettingsRef.");
            return null;
        }

        return this._renderModel.getZeropointSettingsRef();
    }

    public setModelConfiguration(aConfiguration: ConfigurationData) {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not available for setModelConfiguration.");
            return null;
        }

        this._renderModel.setModelConfiguration(aConfiguration);
    }

    public show(aValue: boolean) {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not created. Did you call once cadwork.webgl.SceneAPI.Model.prototype.draw method?");
            return false;
        }

        let groupTree = this._renderModel.getGroupTree();
        if (groupTree) {
            this.setGroupVisibility(groupTree.getRoot().getID(), (aValue === undefined) ? true : aValue);
        }

        this.showHideSettings = (aValue === undefined) ? true : aValue;
    }

    public hide() {
        this.show(false);
    }

    public getShowHideSetting() {
        return this.showHideSettings;
    }

    public selectElementsInGroup(aGroupID: number, aValue: boolean) {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not created. Did you call once cadwork.webgl.SceneAPI.Model.prototype.draw method?");
            return false;
        }

        this._renderModel.selectElementsInGroup(aGroupID, aValue);
    }

    public select(aValue: boolean) {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not created. Did you call once cadwork.webgl.SceneAPI.Model.prototype.draw method?");
            return false;
        }

        this._renderModel.select((aValue === undefined) ? true : aValue);
    }

    public unselect() {
        this.select(false);
    }

    /**
     * Check if group of a group tree model is selected.
     *
     * @param aGroupId Id inside the model with aModelId
     * @return bool
     */
    public isGroupSelected(aGroupID: number): boolean {
        if (!this._renderModel) {
            Debug.logInfo("Render model is not created. Did you call once cadwork.webgl.SceneAPI.Model.prototype.draw method?");
            return false;
        }

        return this._renderModel.isGroupSelected(aGroupID);
    }

    public draw(): boolean {
        if (this._renderModel) {
            Debug.logInfo("Model is already drawn.");
            return false;
        }

        this._renderModel = new RenderModel(
            this._renderDocument.getSceneAPI(),
            this,
            this._renderDocument,
            this.modelUrl,
            this.descriptionUrl,
            this._transformation,
            this.hiddenByDefault
        );

        this._renderDocument.addModel(this._renderModel);
        return true;
    }

    /**
     * Testing function for scene content edit - will be removed soon
     * @param aDiffuseColor
     */

    /*public setModelColor(aDiffuseColor)
    {
        let geometryObjects = this._renderModel.getGeometryObjects();

        for (let i = 0; i != geometryObjects.length; i++) {
            let geometryObject = geometryObjects[i];

            // this should not be like that, the object should have an interface for setting the color, texture etc
            // and it will be inherited through all geometry objects whereas each object can reimplement the interface fncs.
            geometryObject.appearance.material.properties.diffuseColor = vec3.clone(aDiffuseColor);
            this._renderDocument.renderer.updateShape(geometryObject);
        }
    };
    */

    public remove() {
        this._renderDocument.getSceneApiDocument().removeModel(this);

        if (this._modelAggregation) {
            this._modelAggregation.detachObject(this);
            this._modelAggregation = null;
        }

        if (!this._renderModel) {
            return false;
        }

        this._renderDocument.removeModel(this._renderModel);
        this._renderModel = null;
    }

    /***
     * Function transforms the model's local coordinate system (all elements inside this model) using the aTransformation matrix
     * @param aTransformationMatrix transformation matrix of the model applied to the current model data (accumulated transformation)
     * @returns {null}
     */
    public transform(aTransformation: mat4) {
        this._transformation = mat4.clone(aTransformation);

        if (this._renderModel) {
            this._renderModel.transform(aTransformation);
        }
    }

    /**
     * Set model transformation. Comapred to the transform method, this just set model  transformation for realtime usage hence the
     * transformation is not cumulative.
     * @param aTransformation
     */
    public setModelTransformation(aTransformation: mat4) {
        mat4.copy(this._modelTransformation, aTransformation);
    }

    public getModelTransformationRef(): mat4 {
        return this._modelTransformation;
    }

    public enableManipulation() {
        this._manipulationAllowed = true;
    }

    public disableManipulation() {
        this._manipulationAllowed = false;
    }

    public isManipulationAllowed(): boolean {
        return this._manipulationAllowed;
    }

    public setSelectionAllowed(aValue: boolean) {
        this._selectionAllowed = aValue;
    }

    public isSelectionAllowed(): boolean {
        return this._selectionAllowed;
    }

    public getBaseUrl(): string {
        return this._renderModel.getBaseUrl();
    }

    /***
     * Set a manipulation transformation which is not directly appplied to geometry but only applied in shader so
     * the changing of the transformation is interactive
     */
    public applyManipulator() {
        if (this._renderModel) {
            this._renderModel.manipulate(true);
        }
    }

    public cancelManipulator() {
        if (this._renderModel) {
            this._renderModel.manipulate(false);
        }
    }

    public saveToDOM() {
        this._renderModel.saveToDOM();
    }
}
